import type { ContactPageData } from '../api/contactPage';

/** Client-side mirror of backend normalizeMapEmbedUrl for instant preview */
export function normalizeMapEmbedUrl(input?: string | null): string {
  if (input == null) return '';
  let raw = String(input).trim();
  if (!raw) return '';

  const iframeSrc = raw.match(/<iframe[^>]+src=["']([^"']+)["']/i);
  if (iframeSrc) raw = iframeSrc[1].trim();

  raw = raw.replace(/^["']|["']$/g, '');

  if (/\/maps\/embed/i.test(raw)) return raw;

  const coordsOnly = /^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/.exec(raw);
  if (coordsOnly) {
    return `https://maps.google.com/maps?q=${coordsOnly[1]},${coordsOnly[2]}&hl=en&z=15&output=embed`;
  }

  if (!/^https?:\/\//i.test(raw)) {
    return `https://maps.google.com/maps?q=${encodeURIComponent(raw)}&hl=en&z=15&output=embed`;
  }

  try {
    const parsed = new URL(raw);
    const pb = parsed.searchParams.get('pb');
    if (pb) return `https://www.google.com/maps/embed?pb=${pb}`;

    const host = parsed.hostname.replace(/^www\./, '');
    const isGoogleMaps =
      host === 'google.com' ||
      host === 'maps.google.com' ||
      host === 'maps.app.goo.gl' ||
      host === 'goo.gl';

    if (isGoogleMaps) {
      const at = raw.match(/@(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
      if (at) {
        return `https://maps.google.com/maps?q=${at[1]},${at[2]}&hl=en&z=15&output=embed`;
      }

      const q = parsed.searchParams.get('q') || parsed.searchParams.get('query');
      if (q) {
        return `https://maps.google.com/maps?q=${encodeURIComponent(q)}&hl=en&z=15&output=embed`;
      }

      if (parsed.pathname.includes('/place/')) {
        const segment = decodeURIComponent(
          parsed.pathname.split('/place/')[1]?.split('/@')[0]?.replace(/\+/g, ' ') || ''
        );
        if (segment) {
          return `https://maps.google.com/maps?q=${encodeURIComponent(segment)}&hl=en&z=15&output=embed`;
        }
      }

      return `https://maps.google.com/maps?q=${encodeURIComponent(raw)}&hl=en&z=15&output=embed`;
    }
  } catch {
    return `https://maps.google.com/maps?q=${encodeURIComponent(raw)}&hl=en&z=15&output=embed`;
  }

  return raw;
}

export function mapEmbedSrc(map: ContactPageData['map']): string {
  const fromEmbed = normalizeMapEmbedUrl(map.embedUrl);
  if (fromEmbed) return fromEmbed;
  if (map.latitude != null && map.longitude != null) {
    return `https://maps.google.com/maps?q=${map.latitude},${map.longitude}&hl=en&z=15&output=embed`;
  }
  return '';
}
